import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useCallback, useRef, useEffect } from "react";
import type { SseClient } from "../../api/SseClient";
import { createSseClient } from "../../api/factory/CreateSseClient";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type VerifyRequest =
  paths["/proxies/verify"]["post"]["requestBody"]["content"]["application/json"];

type VerifyResponse =
  paths["/proxies/verify"]["post"]["responses"]["200"]["content"]["application/json"];

type ActiveVerificationResponse =
  paths["/proxies/verify/active"]["get"]["responses"]["200"]["content"]["application/json"];

type DeactivateRequest =
  paths["/proxies/deactivate"]["post"]["requestBody"]["content"]["application/json"];

type DeactivateResponse =
  paths["/proxies/deactivate"]["post"]["responses"]["200"]["content"]["application/json"];

type SetOriginRequest =
  paths["/proxies/set-origin"]["post"]["requestBody"]["content"]["application/json"];

type SetOriginResponse =
  paths["/proxies/set-origin"]["post"]["responses"]["200"]["content"]["application/json"];

export type VerificationSelection = VerifyRequest["selection"];

export type ProxyGroup = SetOriginRequest["group"];

export interface ProxyCheckedEvent {
  proxyId: string;
  status: string;
}

export interface ProxyVerificationSummary {
  totalProxies: number;
  checkedCount: number;
  activeCount: number;
  inactiveCount: number;
}

type VerificationState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "progress"; verificationId: string; summary: ProxyVerificationSummary }
  | { kind: "completed"; summary: ProxyVerificationSummary }
  | { kind: "error"; message: string };

type DeactivationState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; deactivatedCount: number }
  | { kind: "error"; message: string };

type SetOriginState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; updatedCount: number }
  | { kind: "error"; message: string };

export interface UseProxyListActionsResult {
  verification: VerificationState;
  deactivation: DeactivationState;
  setOrigin: SetOriginState;
  startVerification: (selection: VerificationSelection, proxyIds: string[]) => void;
  deactivateProxies: (proxyIds: string[]) => void;
  setProxyOrigin: (proxyIds: string[], group: ProxyGroup) => void;
  resetVerification: () => void;
  resetDeactivation: () => void;
  resetSetOrigin: () => void;
}

export function useProxyListActions(onRefetch: () => void): UseProxyListActionsResult {
  const [verification, setVerification] = useState<VerificationState>({ kind: "idle" });
  const [deactivation, setDeactivation] = useState<DeactivationState>({ kind: "idle" });
  const [setOrigin, setSetOrigin] = useState<SetOriginState>({ kind: "idle" });
  const sseClientRef = useRef<SseClient | null>(null);
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  useEffect(() => {
    return () => {
      if (sseClientRef.current !== null) {
        sseClientRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resume() {
      try {
        const response = await fetchWithRefresh("/api/proxies/verify/active");
        if (!response.ok || cancelled) {return;}
        const data = (await response.json()) as ActiveVerificationResponse;
        if (data === null || cancelled) {return;}

        setVerification({
          kind: "progress",
          verificationId: data.verificationId,
          summary: {
            totalProxies: data.totalProxies,
            checkedCount: data.checkedCount,
            activeCount: data.activeCount,
            inactiveCount: data.inactiveCount,
          },
        });
        connectSse(data.verificationId, data.totalProxies);
      } catch (error: unknown) {
        // Восстановление подписки — попытка без гарантий: экран остаётся в
        // исходном состоянии, а причина уходит в журнал.
        logger.warn({ err: error, component: "useProxyListActions" }, "Failed to resume proxy verification");
      }
    }

    void resume();
    return () => {
      cancelled = true;
    };
  }, []);

  function connectSse(verificationId: string, totalProxies: number) {
    if (sseClientRef.current !== null) {
      sseClientRef.current.close();
    }
    const client = createSseClient({
      url: `/api/proxies/verify/${verificationId}/subscribe`,
      events: {
        "verification:snapshot"(raw: unknown) {
          const seconds = raw as { checkedCount: number; activeCount: number; inactiveCount: number };
          setVerification({
            kind: "progress",
            verificationId,
            summary: {
              totalProxies,
              checkedCount: seconds.checkedCount,
              activeCount: seconds.activeCount,
              inactiveCount: seconds.inactiveCount,
            },
          });
        },
        "proxy:checked"(raw: unknown) {
          const event = raw as ProxyCheckedEvent;
          setVerification((previous) => {
            let next = previous;
            if (previous.kind === "progress") {
              const updated = { ...previous.summary, checkedCount: previous.summary.checkedCount + 1 };
              if (event.status === "active") {
                updated.activeCount = previous.summary.activeCount + 1;
              } else {
                updated.inactiveCount = previous.summary.inactiveCount + 1;
              }
              next = { ...previous, summary: updated };
            }
            return next;
          });
        },
        "verification:completed"(raw: unknown) {
          const completed = raw as {
            totalProxies: number;
            checkedCount: number;
            activeCount: number;
            inactiveCount: number;
          };
          setVerification({
            kind: "completed",
            summary: {
              totalProxies: completed.totalProxies,
              checkedCount: completed.checkedCount,
              activeCount: completed.activeCount,
              inactiveCount: completed.inactiveCount,
            },
          });
          if (sseClientRef.current !== null) {
            sseClientRef.current.close();
          }
          sseClientRef.current = null;
          onRefetchRef.current();
        },
      },
      onError() {
        setVerification({ kind: "error", message: "Потеряно соединение с сервером" });
      },
    });
    sseClientRef.current = client;
  }

  const startVerification = useCallback(
    (selection: VerificationSelection, proxyIds: string[]) => {
      void (async () => {
        setVerification({ kind: "starting" });

        try {
          const body: VerifyRequest = { selection, proxyIds };
          const response = await fetchWithRefresh("/api/proxies/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = (await response.json()) as VerifyResponse;

          if (data.totalProxies === 0) {
            setVerification({ kind: "completed", summary: { totalProxies: 0, checkedCount: 0, activeCount: 0, inactiveCount: 0 } });
            return;
          }

          connectSse(data.verificationId, data.totalProxies);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to start verification");
          setVerification({ kind: "error", message });
        }
      })();
    },
    [],
  );

  const deactivateProxies = useCallback(
    (proxyIds: string[]) => {
      void (async () => {
        setDeactivation({ kind: "loading" });

        try {
          const body: DeactivateRequest = { proxyIds };
          const response = await fetchWithRefresh("/api/proxies/deactivate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = (await response.json()) as DeactivateResponse;
          setDeactivation({ kind: "success", deactivatedCount: data.deactivatedCount });
          onRefetchRef.current();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to deactivate proxies");
          setDeactivation({ kind: "error", message });
        }
      })();
    },
    [],
  );

  const setProxyOrigin = useCallback(
    (proxyIds: string[], group: ProxyGroup) => {
      void (async () => {
        setSetOrigin({ kind: "loading" });

        try {
          const body: SetOriginRequest = { proxyIds, group };
          const response = await fetchWithRefresh("/api/proxies/set-origin", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = (await response.json()) as SetOriginResponse;
          setSetOrigin({ kind: "success", updatedCount: data.updatedCount });
          onRefetchRef.current();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to set proxy origin");
          setSetOrigin({ kind: "error", message });
        }
      })();
    },
    [],
  );

  const resetVerification = useCallback(() => {
    if (sseClientRef.current !== null) {
      sseClientRef.current.close();
    }
    sseClientRef.current = null;
    setVerification({ kind: "idle" });
  }, []);

  const resetDeactivation = useCallback(() => {
    setDeactivation({ kind: "idle" });
  }, []);

  const resetSetOrigin = useCallback(() => {
    setSetOrigin({ kind: "idle" });
  }, []);

  return {
    verification,
    deactivation,
    setOrigin,
    startVerification,
    deactivateProxies,
    setProxyOrigin,
    resetVerification,
    resetDeactivation,
    resetSetOrigin,
  };
}
